import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { navigateTo } from '../../services/router.js';
import { AuthStateController } from '../../services/auth-state.js';
import { wsClient } from '../../services/websocket-client.js';

interface NavItem {
  label: string;
  icon: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Cockpit', icon: '\u25C9', path: '/jarvis' },
  { label: 'Dashboard', icon: '\u25A0', path: '/board' },
  { label: 'Meine Arbeit', icon: '\u25B6', path: '/projects' },
  { label: 'Meine Firmen', icon: '\u25C6', path: '/companies' },
  { label: 'Signale', icon: '\u25C8', path: '/signals' },
  { label: 'Ziele', icon: '\u2605', path: '/goals' },
  { label: 'Decisions', icon: '\u27D0', path: '/decisions' },
  { label: 'Playbooks', icon: '\u2442', path: '/playbooks' },
  { label: 'Freigaben', icon: '\u2713', path: '/approvals' },
  { label: 'Kommunikation', icon: '\u2709', path: '/ceo-chats' },
  { label: 'Convo Coach', icon: '\u25C9', path: '/conversations' },
  { label: 'Lebensplan', icon: '\u2605', path: '/lifeplan' },
  { label: 'System', icon: '\u2699', path: '/settings' },
];

/** Maps sidebar items to all routes that should highlight them */
const ACTIVE_PATH_MAP: Record<string, string[]> = {
  '/jarvis': ['/jarvis'],
  '/board': ['/board'],
  '/approvals': ['/approvals'],
  '/projects': ['/projects', '/todos'],
  '/companies': ['/companies', '/founder-board'],
  '/signals': ['/signals'],
  '/goals': ['/goals'],
  '/decisions': ['/decisions'],
  '/playbooks': ['/playbooks'],
  '/ceo-chats': ['/ceo-chats', '/messages', '/inbox', '/people'],
  '/conversations': ['/conversations'],
  '/lifeplan': ['/lifeplan'],
  '/settings': ['/settings', '/agents', '/team', '/skills', '/cron', '/monitoring'],
};

/** Check if a nav item should be highlighted for the current path */
function isNavActive(navPath: string, currentPath: string): boolean {
  const paths = ACTIVE_PATH_MAP[navPath] ?? [navPath];
  return paths.some(p => currentPath === p || currentPath.startsWith(p + '/'));
}

@customElement('cp-sidebar')
export class CpSidebar extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) activePath = '/board';
  @property({ type: Boolean, reflect: true }) open = false;
  @state() private _inboxCount = 0;

  private _authState = new AuthStateController(this);
  private _wsMessageHandler: ((e: Event) => void) | null = null;

  override connectedCallback() {
    super.connectedCallback();
    // Fetch initial pending count
    fetch('/api/inbox/count')
      .then(r => r.json())
      .then((data: { count: number }) => { this._inboxCount = data.count; })
      .catch(() => { /* ignore fetch errors */ });

    // Subscribe to real-time inbox events via WebSocket
    this._wsMessageHandler = (e: Event) => {
      const msg = (e as CustomEvent<{ type: string; payload: unknown }>).detail;
      if (msg.type === 'inbox:item-received') {
        this._inboxCount++;
      } else if (msg.type === 'inbox:item-accepted' || msg.type === 'inbox:item-rejected') {
        this._inboxCount = Math.max(0, this._inboxCount - 1);
      }
    };
    wsClient.addEventListener('ws-message', this._wsMessageHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._wsMessageHandler) {
      wsClient.removeEventListener('ws-message', this._wsMessageHandler);
      this._wsMessageHandler = null;
    }
  }

  private _handleNavClick(path: string, e: Event) {
    e.preventDefault();
    navigateTo(path);
    this.dispatchEvent(new CustomEvent('sidebar-nav-click', { bubbles: true, composed: true }));
  }

  private _handleLogout() {
    void this._authState.logout();
  }

  render() {
    const user = this._authState.currentUser;
    const badgeLabel = this._inboxCount > 99 ? '99+' : String(this._inboxCount);

    return html`
      <aside class="cp-sidebar ${this.open ? 'cp-sidebar--open' : ''}">
        <div class="cp-sidebar__brand">
          <img class="cp-sidebar__brand-mark" src="/pelorus-mark.svg" alt="" />
          <span class="cp-sidebar__brand-name">PELORUS</span>
        </div>
        <nav class="cp-sidebar__nav">
          <ul class="cp-sidebar__nav-list">
            ${NAV_ITEMS.map(item => html`
              <li class="cp-sidebar__nav-item ${isNavActive(item.path, this.activePath) ? 'cp-sidebar__nav-item--active' : ''}">
                <a
                  class="cp-sidebar__nav-link"
                  href="#${item.path}"
                  @click=${(e: Event) => this._handleNavClick(item.path, e)}
                >
                  <span class="cp-sidebar__nav-icon">${item.icon}</span>
                  <span class="cp-sidebar__nav-label">${item.label}</span>
                  ${item.path === '/messages' && this._inboxCount > 0 ? html`<span class="cp-sidebar__badge">${badgeLabel}</span>` : nothing}
                </a>
              </li>
            `)}
          </ul>
          <cp-connection-status></cp-connection-status>
          ${user ? html`
            <div class="cp-sidebar__separator"></div>
            <div class="cp-sidebar__user">
              ${user.avatar_url ? html`
                <img class="cp-sidebar__user-avatar" src="${user.avatar_url}" alt="${user.name}" />
              ` : html`
                <div class="cp-sidebar__user-avatar cp-sidebar__user-avatar--placeholder">
                  ${user.name.charAt(0).toUpperCase()}
                </div>
              `}
              <span class="cp-sidebar__user-name">${user.name}</span>
              <button
                class="cp-sidebar__user-logout"
                @click=${this._handleLogout}
                title="Abmelden"
              >&#x2192;</button>
            </div>
          ` : nothing}
        </nav>
      </aside>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-sidebar': CpSidebar;
  }
}
